import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './en.json'
import ta from './ta.json'

/**
 * Static UI text only.
 *
 * Stage 2 §1 is emphatic about the split: labels, buttons and messages live in
 * these bundles and are written once by Trustline. Dynamic master data
 * (vehicle type names, charge heads) is resolved server-side from its `name` JSONB
 * against the user's preferred_language — it is NEVER shipped in these files, and
 * no form in the app offers a per-language text box for a tenant user to fill in.
 *
 * Adding a language is dropping in a bundle and registering it here — no code
 * change beyond this map, and no schema change at all.
 */
export const RESOURCE_BUNDLES = {
  en: { translation: en },
  ta: { translation: ta },
} as const

export type SupportedLanguage = keyof typeof RESOURCE_BUNDLES

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: 'English',
  ta: 'தமிழ்',
}

export const STORAGE_KEY = 'tfleet.language'

/**
 * Stage 3 §2.1: the language selector sits on the login screen and takes effect
 * before login completes, so the initial language comes from local storage or the
 * browser rather than from the user record.
 */
function initialLanguage(): SupportedLanguage {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && stored in RESOURCE_BUNDLES) return stored as SupportedLanguage
  // Tamil-first for the target user base (Stage 1 §6), but only when the browser
  // actually asks for it.
  return navigator.language.startsWith('ta') ? 'ta' : 'en'
}

void i18next.use(initReactI18next).init({
  resources: RESOURCE_BUNDLES,
  lng: initialLanguage(),
  // Stage 2 §5.5: fall back to English when a key is missing for the user's
  // language, rather than rendering a raw key at a transport office clerk.
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnNull: false,
})

export function setLanguage(language: SupportedLanguage): void {
  localStorage.setItem(STORAGE_KEY, language)
  void i18next.changeLanguage(language)
  document.documentElement.lang = language
}

export default i18next
